import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { StyledButton } from "@insite/mobius/Clickable";
import getColor from "@insite/mobius/utilities/getColor";
import { preventBackNavigation } from "@NA/Utils/Utility";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    pageType: getCurrentPage(state).type,
    isAuthenticated: state.context.session!.isAuthenticated,
});

const mapDispatchToProps = {
    signOut,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const enum fields {
    alignment = "alignment",
    title = "title",
    links = "links",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.links]: LinkModel[];
    };
    extendedStyles?: ADIFooterBarCopyrightStyles;
}

export interface ADIFooterBarCopyrightStyles {
    linkListWrapper?: InjectableCss;
    rightLinksWrapper?: InjectableCss;
}

export const adiFooterBarCopyrightStyles: ADIFooterBarCopyrightStyles = {
    linkListWrapper: {
        css: css`
            padding-top: 5px;
            padding-left: 0px;
            > div[class^="sc"] {
                .copyrightLinksWrapper {
                    > ${StyledButton} {
                        &:before {
                            font-size: 14px;
                            color: ${getColor("common.background")};
                            font-weight: 800;
                        }
                        &:focus {
                            outline: none;
                        }
                        > ${TypographyStyle} {
                            text-decoration: underline;
                            &:hover {
                                font-weight: bold;
                            }
                        }
                    }
                }
                .cookiePref {
                    &:before {
                        font-size: 14px;
                        color: #ffffff;
                        font-weight: 800;
                    }
                    a {
                        text-decoration: underline;
                        color: #ffffff; // P21 change
                        &:hover {
                            font-weight: bold;
                        }
                    }
                }
                .title-wrapper {
                    ${TypographyStyle} {
                        margin-left: 0px;
                        font-weight: 600;
                        font-size: 12px;
                    }
                }
                ${TypographyStyle} {
                    color: ${getColor("common.background")};
                    font-size: 12px;
                    font-weight: 600;
                    margin: 0px 12px 0px;
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`                                                                       
                                            margin: 0px 5px 0px;
                                        `,
                    css`                                                                       
                                            margin: 0px 5px 0px;
                                        `,
                    css`                                                                       
                                           margin: 0px 5px 0px;
                                        `,
                    null,
                    null,
                ])}
                }
            }
        `,
    },
    rightLinksWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            margin-left: 10px;
            line-height: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 2px !important;
                            justify-content: center;
                        `,
                        css`
                            margin-top: 16px;
                            justify-content: center;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
};

export const ADIFooterBarCopyrightWidget: FC<Props> = ({
    fields,
    extendedStyles,
    pageType,
    signOut,
    isAuthenticated,
}) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);
    const styles = useMergeStyles("linkList", adiFooterBarCopyrightStyles, extendedStyles);

    const alignmentStyles = css`
        text-align: ${fields.alignment || "right"};
        ${styles.linkListWrapper?.css}
        @media only screen and (min-width: 1081px) and (max-width: 1500px) {
            width: auto !important;
            margin-left: 10px !important;
        }
    `;

    const onSignOutHandler = () => {
        if (window) {
            window.sessionStorage.removeItem("adi_pricespider_webtoken");
        }
        signOut();
        preventBackNavigation();
    };

    return (
        <StyledWrapper>
            <StyledWrapper data-test-selector="copyrightFooterLinks" css={alignmentStyles}>
                <StyledWrapper {...styles.rightLinksWrapper}>
                    <StyledWrapper className="title-wrapper">
                        <Typography>{fields.title}</Typography>
                    </StyledWrapper>
                    {links.map((link, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <StyledWrapper className="copyrightLinksWrapper" key={index}>
                            {link.url && (
                                <>
                                    <Link
                                        className="list-link"
                                        href={link.url}
                                        target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                    >
                                        {fields.links[index].fields.overriddenTitle || link.title}
                                    </Link>
                                </>
                            )}
                        </StyledWrapper>
                    ))}
                    <StyledWrapper className="copyrightLinksWrapper cookiePref">
                        <Typography dangerouslySetInnerHTML={{ __html: '<span id="teconsent"> </span>' }}></Typography>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ADIFooterBarCopyrightWidget),
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        displayName: "ADI Footer Bar Copyright Widget",
        fieldDefinitions: [
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
